
import React, { useState } from 'react';
import Modal from './Modal';
import Button from '../ui/Button';
import FileUpload from '../ui/FileUpload';
import RichTextEditor from '../ui/RichTextEditor';

interface GlossaryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: GlossaryData) => void;
}

interface GlossaryData {
  term: string;
  definition: string;
  attachments: File[];
}

const GlossaryModal: React.FC<GlossaryModalProps> = ({ isOpen, onClose, onSubmit }) => {
  const [formData, setFormData] = useState<GlossaryData>({
    term: '',
    definition: '',
    attachments: []
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.term.trim()) {
      newErrors.term = 'Term is required';
    }

    if (!formData.definition.trim()) {
      newErrors.definition = 'Definition is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      onSubmit(formData);
      handleReset();
      onClose();
    } catch (error) {
      console.error('Error creating glossary entry:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setFormData({
      term: '',
      definition: '',
      attachments: []
    });
    setErrors({});
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add Glossary Entry" size="lg">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Term */}
        <div>
          <label htmlFor="term" className="block text-sm font-medium text-gray-700 mb-2">
            📖 Term/Concept *
          </label>
          <input
            type="text"
            id="term"
            value={formData.term}
            onChange={(e) => setFormData(prev => ({ ...prev, term: e.target.value }))}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.term ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Enter term or concept name"
          />
          {errors.term && <p className="mt-1 text-sm text-red-600">{errors.term}</p>}
        </div>

        {/* Definition */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Definition & Description *
          </label>
          <RichTextEditor
            value={formData.definition}
            onChange={(value) => setFormData(prev => ({ ...prev, definition: value }))}
            placeholder="Enter detailed definition and description..."
            minHeight="200px"
          />
          {errors.definition && <p className="mt-1 text-sm text-red-600">{errors.definition}</p>}
        </div>

        {/* Supporting Files */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Supporting Files (Optional)
          </label>
          <FileUpload
            onFilesSelected={(files) => setFormData(prev => ({ ...prev, attachments: files }))}
            acceptedTypes={['application/pdf', 'image/*', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document']}
            label="Upload supporting documents, images, or references"
          />
          <p className="mt-1 text-xs text-gray-500">
            Accepted formats: PDF, Images, Word documents
          </p>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? (
              <span className="flex items-center">
                <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                Adding...
              </span>
            ) : (
              'Add to Glossary'
            )}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default GlossaryModal;
